import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import _ from "lodash";
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useHistory } from "react-router-dom";
import InputField from "../../partials/form_fields/InputField";
import SelectItems from "../../partials/form_fields/SelectItems";
import Select from 'react-select';
import selectStyle from "../../partials/form_fields/selectStyle";
import useFetch from "../../../customHooks/useFetch";
import useQuery from "../../../customHooks/useQuery";
import {
  createLunchCheckoutPublic,
  getLunchPaymentStatusPublic,
  checkLunchPaymentByEmailPublic,
} from "../../../actions/payment";

import styles from './register.module.scss';

const emailReg = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,7}$/i;
const REGISTER_DRAFT_KEY = 'scarmor_register_draft';

function normalizeDateKey(value) {
  if (value === null || value === undefined || value === '' || value === 'cocktail') {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toISOString().slice(0, 10);
}

function normalizeDateList(values) {
  if (!Array.isArray(values)) return [];
  const seen = new Set();
  const result = [];
  values.forEach((value) => {
    const key = normalizeDateKey(value);
    if (!key || seen.has(key)) return;
    seen.add(key);
    result.push(key);
  });
  return result;
}

function listIncludesDate(list, date) {
  const key = normalizeDateKey(date);
  return !!key && normalizeDateList(list).includes(key);
}

export const createUser = async (data, exhibition) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/company/request`,
      data,
      {
        method: 'POST',
        headers: {
          "Authorization": localStorage.getItem('token'),
          "Content-Type": "application/json",
          exhibition,
        },
        body: data
      }
    );
    return response;
  } catch (error) {
    throw error.response.data;
  }
};

const Register = ({ history }) => {
  const routerHistory = useHistory();
  const query = useQuery();
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({});
  const [lunchPaid, setLunchPaid] = useState(false);
  const [lunchDays, setLunchDays] = useState([]);
  const [lunchPaidDays, setLunchPaidDays] = useState([]);
  const [selectedPresenceDays, setSelectedPresenceDays] = useState([]);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [lunchPaymentReadyEmail, setLunchPaymentReadyEmail] = useState(null);

  const {
    data: listExhibitions,
  } = useFetch(
    `${process.env.REACT_APP_API_URL}/company/list/public/exhibition`,
    true,
    null,
  );

  const {
    data: listCompany,
  } = useFetch(
    `${process.env.REACT_APP_API_URL}/company/list/public?type=${formData?.type?.toLowerCase()}`,
    !!formData?.exhibition?.value && !!formData.type,
    formData?.exhibition?.value,
  );

  const {
    days = [],
    exhibitions = [],
    companies = [],
    activities = [],
    lunch: lunchConfig = {},
  } = {
    ...(listExhibitions || {}),
    ...(listCompany || {}),
  };

  const salonDays = useMemo(() => {
    return (days || [])
      .filter((d) => d.value !== 'cocktail')
      .map((day) => ({
        value: day.value,
        label: format(new Date(day.value), 'd MMMM', { locale: fr }),
      }));
  }, [days]);

  const isMultiDaySalon = salonDays.length > 1;
  const isPaidLunchExhibition = !!(lunchConfig.isPaid && lunchConfig.price > 0);
  const isLunchSelectionAllowed = !isPaidLunchExhibition || lunchConfig.paymentEnabled !== false;
  const isLunchPaymentRequired = formData?.type === 'Fournisseur' && isPaidLunchExhibition && isLunchSelectionAllowed;
  const lunchPrice = lunchConfig.price;

  const unpaidLunchDays = useMemo(() => {
    return normalizeDateList(lunchDays).filter((day) => !listIncludesDate(lunchPaidDays, day));
  }, [lunchDays, lunchPaidDays]);

  const wantsLunch = isMultiDaySalon && formData?.type === 'Fournisseur'
    ? lunchDays.length > 0
    : formData?.lunch === 'Oui';

  const hasPresence = isMultiDaySalon && formData?.type === 'Fournisseur'
    ? selectedPresenceDays.length > 0
    : formData?.presence === 'Oui';

  const needsPayment = isLunchPaymentRequired
    && hasPresence
    && wantsLunch
    && (isMultiDaySalon ? unpaidLunchDays.length > 0 : !lunchPaid);

  const paymentTotal = (lunchPrice || 0) * (isMultiDaySalon ? unpaidLunchDays.length : 1);

  function saveFormDraft() {
    sessionStorage.setItem(REGISTER_DRAFT_KEY, JSON.stringify({
      formData,
      lunchPaid,
      lunchDays,
      lunchPaidDays,
      selectedPresenceDays,
    }));
  }

  function restoreFormDraft() {
    const raw = sessionStorage.getItem(REGISTER_DRAFT_KEY);
    if (!raw) return false;

    try {
      const draft = JSON.parse(raw);
      if (draft.formData) setFormData(draft.formData);
      if (draft.lunchPaid !== undefined) setLunchPaid(draft.lunchPaid);
      if (draft.lunchDays) setLunchDays(normalizeDateList(draft.lunchDays));
      if (draft.lunchPaidDays) setLunchPaidDays(normalizeDateList(draft.lunchPaidDays));
      if (draft.selectedPresenceDays) setSelectedPresenceDays(draft.selectedPresenceDays);
      return true;
    } catch (error) {
      return false;
    }
  }

  function clearFormDraft() {
    sessionStorage.removeItem(REGISTER_DRAFT_KEY);
  }

  useEffect(() => {
    async function handleLunchPaymentReturn() {
      const paymentStatus = query.get('lunch_payment');
      const sessionId = query.get('session_id');

      if (paymentStatus === 'success' && sessionId) {
        const res = await getLunchPaymentStatusPublic(sessionId);
        restoreFormDraft();
        if (res?.lunchPaid) {
          const paid = normalizeDateList(res.paidDates || res.lunchPaidDays || []);
          if (paid.length) {
            setLunchPaidDays((prev) => normalizeDateList([...prev, ...paid]));
            setLunchDays((prev) => normalizeDateList([...prev, ...paid]));
          }
          setLunchPaid(true);
          setLunchPaymentReadyEmail(res.email);
          setFormData((prev) => ({ ...prev, lunch: 'Oui', presence: prev.presence || 'Oui' }));
        }
        routerHistory.replace('/register');
      } else if (paymentStatus === 'cancel') {
        restoreFormDraft();
        routerHistory.replace('/register');
      }
    }

    handleLunchPaymentReturn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (lunchPaymentReadyEmail && formData.email === lunchPaymentReadyEmail) {
      setLunchPaid(true);
      setFormData((prev) => ({ ...prev, lunch: 'Oui' }));
    }
  }, [lunchPaymentReadyEmail, formData.email]);

  useEffect(() => {
    let cancelled = false;

    async function checkEmailLunchPayment() {
      if (
        !isLunchPaymentRequired
        || !wantsLunch
        || !hasPresence
        || !formData?.email
        || !emailReg.test(formData.email)
        || !formData?.exhibition?.value
      ) {
        return;
      }

      if (lunchPaymentReadyEmail && formData.email === lunchPaymentReadyEmail) {
        setLunchPaid(true);
        return;
      }

      const res = await checkLunchPaymentByEmailPublic(
        formData.exhibition.value,
        formData.email,
        null,
        isMultiDaySalon ? lunchDays : undefined
      );
      if (!cancelled) {
        const paidDays = normalizeDateList(res?.lunchPaidDays);
        if (paidDays.length) {
          setLunchPaidDays(paidDays);
        }
        if (isMultiDaySalon) {
          setLunchPaid(
            lunchDays.length > 0
            && lunchDays.every((d) => listIncludesDate(paidDays, d))
          );
        } else {
          setLunchPaid(!!res?.lunchPaid);
        }
      }
    }

    checkEmailLunchPayment();
    return () => {
      cancelled = true;
    };
  }, [
    formData.email,
    formData.exhibition?.value,
    formData.lunch,
    formData.presence,
    lunchDays,
    wantsLunch,
    hasPresence,
    isMultiDaySalon,
    isLunchPaymentRequired,
    lunchPaymentReadyEmail,
  ]);

  useEffect(() => {
    if (formData?.type !== 'Fournisseur' || !formData?.company?.value || !companies.length) {
      return;
    }

    const companyOption = companies.find((c) => c.value === formData.company.value);
    if (!companyOption) return;

    setFormData((prev) => {
      if (prev.type !== 'Fournisseur' || prev.company?.value !== companyOption.value) {
        return prev;
      }

      const siret = companyOption.siret || '';
      const vatNumber = companyOption.vatNumber || '';

      if (!siret && !vatNumber) return prev;
      if (prev.siret === siret && prev.vatNumber === vatNumber) return prev;

      return {
        ...prev,
        siret: prev.siret || siret,
        vatNumber: prev.vatNumber || vatNumber,
      };
    });
  }, [companies, formData?.company?.value, formData?.type]);

  // Keep lunch days within selected presence for multi-day fournisseur
  useEffect(() => {
    if (!isMultiDaySalon || formData?.type !== 'Fournisseur') return;
    const presenceKeys = normalizeDateList(selectedPresenceDays);
    setLunchDays((prev) => {
      const filtered = prev.filter((day) => presenceKeys.includes(day));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [selectedPresenceDays, isMultiDaySalon, formData?.type]);

  function handleChange(val, error, field){
    setErrorMessage('');
    let updatedData = {...formData}

    if (field === 'presence' && val === 'Non') {
      _.set(updatedData, 'lunch', null)
      setLunchPaid(false)
      setLunchDays([])
      setSelectedPresenceDays([])
    }

    if (field === 'exhibition') {
      _.set(updatedData, 'company', null)
      setLunchPaid(false)
      setLunchDays([])
      setLunchPaidDays([])
      setSelectedPresenceDays([])
      setLunchPaymentReadyEmail(null)
      _.unset(updatedData, 'siret');
      _.unset(updatedData, 'vatNumber');
    }

    if (field === 'type' || field === 'company') {
      setLunchPaid(false)
      setLunchPaymentReadyEmail(null)
    }

    if (field === 'company' && val && updatedData.type === 'Fournisseur') {
      const companyOption = companies.find((c) => c.value === val.value) || val;
      _.set(updatedData, 'siret', companyOption.siret || '');
      _.set(updatedData, 'vatNumber', companyOption.vatNumber || '');
    }

    if (field === 'type') {
      _.unset(updatedData, 'siret');
      _.unset(updatedData, 'vatNumber');
      setSelectedPresenceDays([]);
      setLunchDays([]);
      if (val === 'Fournisseur' && updatedData.company) {
        const companyOption = companies.find((c) => c.value === updatedData.company.value);
        if (companyOption) {
          _.set(updatedData, 'siret', companyOption.siret || '');
          _.set(updatedData, 'vatNumber', companyOption.vatNumber || '');
        }
      }
    }

    if (field === 'lunch') {
      if (val === 'Non') {
        setLunchPaid(false)
        setLunchDays([])
      } else if (lunchPaymentReadyEmail && updatedData.email === lunchPaymentReadyEmail) {
        setLunchPaid(true)
      } else if (!lunchPaid) {
        setLunchPaid(false)
      }
    }

    if (field === 'email' && val !== lunchPaymentReadyEmail) {
      setLunchPaid(false)
    }

    if (field === 'presenceDays' && Array.isArray(val)) {
      const found = salonDays.filter((day) => val.includes(day.label));
      const updatedDays = found.map((d) => d.value);
      setSelectedPresenceDays(updatedDays);
      _.set(updatedData, 'presence', updatedDays.length > 0 ? 'Oui' : null);
      setFormData(updatedData);
      return;
    }

    _.set(updatedData, field, val)
    setFormData(updatedData)
  }

  function handleLunchDaysChange(labels) {
    if (!isLunchSelectionAllowed) return;
    const found = salonDays.filter((day) => (labels || []).includes(day.label));
    const nextDays = found
      .map((day) => normalizeDateKey(day.value) || day.value)
      .filter((day) => listIncludesDate(selectedPresenceDays, day));
    setLunchDays(nextDays);
    setFormData((prev) => ({
      ...prev,
      lunch: nextDays.length > 0 ? 'Oui' : 'Non',
    }));
    const unpaid = nextDays.filter((d) => !listIncludesDate(lunchPaidDays, d));
    setLunchPaid(nextDays.length > 0 && unpaid.length === 0);
  }

  const isValid = useMemo(() => {
    let bool = false;
    const multiDayPresenceOk = selectedPresenceDays.length > 0;

    if (
      formData?.exhibition
      && formData?.company
      && formData.type === 'Magasin'
      && formData.profile?.lastName
      && formData.profile?.firstName
      && (
        formData.email &&
        emailReg.test(formData.email)
      )
      && formData.profile?.function
      && (formData.profile?.activity || []).length > 0
      && (
        (formData?.presence === 'Oui' && formData.lunch)
        || (formData?.presence === 'Non')
      )
    ) {
      bool = true;
    } else if (
      formData?.exhibition
      && formData?.company
      && formData?.type === 'Fournisseur'
      && formData.profile?.lastName
      && formData.profile?.firstName
      && (
        formData.email &&
        emailReg.test(formData.email)
      )
      && formData.profile?.function
      && formData.siret?.trim()
      && formData.vatNumber?.trim()
      && (
        isMultiDaySalon
          ? multiDayPresenceOk
          : (
            (formData?.presence === 'Oui' && formData.lunch)
            || (formData?.presence === 'Non')
          )
      )
    ) {
      bool = true;
    }

    return bool;
  }, [formData, isMultiDaySalon, selectedPresenceDays]);

  const canSubmit = isValid && !needsPayment;

  async function handleLunchPayment() {
    if (
      !emailReg.test(formData.email)
      || !formData?.company?.value
      || !formData?.exhibition?.value
      || isPaymentLoading
    ) {
      return;
    }

    const datesToPay = isMultiDaySalon
      ? unpaidLunchDays
      : (salonDays[0] ? [normalizeDateKey(salonDays[0].value) || salonDays[0].value] : undefined);

    setIsPaymentLoading(true);
    try {
      const response = await createLunchCheckoutPublic(formData.exhibition.value, {
        email: formData.email,
        companyId: formData.company.value,
        dates: datesToPay,
      });

      if (response?.url) {
        saveFormDraft();
        window.location.href = response.url;
      }
    } finally {
      setIsPaymentLoading(false);
    }
  }

  const handleSubmit = useCallback(() => {
    const type = formData.type;
    if (!canSubmit || !['Magasin', 'Fournisseur'].includes(type)) return;
    setErrorMessage('');

    let payload = {};
    payload.company = formData.company.value;
    if (type === 'Fournisseur') {
      payload.siret = formData.siret.trim();
      payload.vatNumber = formData.vatNumber.trim();
    }

    let presence = [];
    if (isMultiDaySalon && type === 'Fournisseur') {
      presence = selectedPresenceDays;
    } else {
      presence = formData.presence === 'Oui' ? days.map((d) => d.value) : [];
    }

    payload.user = {
      email: formData.email,
      profile: {
        ...formData.profile,
      },
      presence,
    };
    
    if (type === 'Magasin') {
      payload.user.profile.activity = (formData?.activity || []).map((d) => d.value);
      payload.user.lunch = formData.presence === 'Oui' ? (formData.lunch === 'Oui') : false;
    } else if (type === 'Fournisseur') {
      if (isMultiDaySalon) {
        payload.user.lunchDays = normalizeDateList(lunchDays);
        payload.user.lunch = lunchDays.length > 0;
      } else {
        payload.user.lunch = formData.presence === 'Oui' ? (formData.lunch === 'Oui') : false;
        if (payload.user.lunch && salonDays[0]) {
          payload.user.lunchDays = [normalizeDateKey(salonDays[0].value) || salonDays[0].value];
        }
      }
    }

    createUser(payload, formData?.exhibition?.value).then(() => {
      clearFormDraft();
      history.push('/register-confirmation')
    }).catch((responseError) => {
      console.log(responseError);
      let msg = responseError.error;
      if (responseError.error === 'That email address is already in use.') {
        msg = 'Cette email est déjà utilisé.';
      } else if (responseError.error === 'Paiement du déjeuner requis.') {
        msg = 'Le paiement du déjeuner est requis avant de soumettre l\'inscription.';
      } else if (responseError.error === 'Le numéro de SIRET et le numéro de TVA sont obligatoires.') {
        msg = responseError.error;
      }
      setErrorMessage(msg);
    })
  }, [canSubmit, formData, days, history, isMultiDaySalon, selectedPresenceDays, lunchDays, salonDays]);

  const lunchItems = isLunchSelectionAllowed ? ['Oui', 'Non'] : ['Non'];

  function getSelectedPresenceLabels() {
    return salonDays
      .filter((day) => selectedPresenceDays.some((selected) => normalizeDateKey(selected) === normalizeDateKey(day.value)))
      .map((day) => day.label);
  }

  function getSelectedLunchLabels() {
    return salonDays
      .filter((day) => listIncludesDate(lunchDays, day.value))
      .map((day) => day.label);
  }

  function getPaidLunchLabels() {
    return salonDays
      .filter((day) => listIncludesDate(lunchDays, day.value) && listIncludesDate(lunchPaidDays, day.value))
      .map((day) => day.label);
  }

  function renderPresenceAndLunch() {
    // Multi-day + Fournisseur: day pickers
    if (isMultiDaySalon && formData?.type === 'Fournisseur') {
      const lunchDayOptions = salonDays.filter((day) =>
        selectedPresenceDays.some((selected) => normalizeDateKey(selected) === normalizeDateKey(day.value))
      );
      const paidLunchLabels = getPaidLunchLabels();

      return (
        <>
          <div className={styles.containerField}>
            <label>Jours de présence sur le salon<span className="required">*</span></label>
            <SelectItems
              items={salonDays.map((day) => day.label)}
              selectedItems={getSelectedPresenceLabels()}
              uniq={false}
              handleChange={(val) => handleChange(val, false, "presenceDays")}
            />
          </div>
              {selectedPresenceDays.length > 0 && (
            <div className={styles.containerField}>
              <label>Jours de déjeuner</label>
              {!isLunchSelectionAllowed &&
                <p className={styles.warn}>
                  L'inscription au repas n'est plus possible
                </p>
              }
              <div className={!isLunchSelectionAllowed ? styles.disabledSelect : undefined}>
                <SelectItems
                  items={lunchDayOptions.map((day) => day.label)}
                  selectedItems={getSelectedLunchLabels()}
                  uniq={false}
                  handleChange={(val) => handleLunchDaysChange(val)}
                />
              </div>
              {paidLunchLabels.length > 0 &&
                <p className={styles.lunchPaid}>
                  Payé : {paidLunchLabels.join(', ')}
                </p>
              }
            </div>
          )}
        </>
      );
    }

    // Single-day or Magasin: legacy Oui/Non
    return (
      <>
        <div className={styles.containerField}>
          <label>Sera présent sur le salon<span className="required">*</span></label>
          <SelectItems
            items={['Oui', 'Non']}
            selectedItems={formData?.presence ? [formData?.presence] : []}
            uniq={true}
            handleChange={(val) => handleChange(val,false,"presence")}
          />
        </div>
        {(formData?.presence === 'Oui') && (
          <div className={styles.containerField}>
            <label>Participera au déjeuner<span className="required">*</span></label>
            {!isLunchSelectionAllowed && formData?.type === 'Fournisseur' &&
              <p className={styles.warn}>
                L'inscription au repas n'est plus possible
              </p>
            }
            <SelectItems
              items={lunchItems}
              selectedItems={formData?.lunch ? [formData?.lunch] : []}
              uniq={true}
              handleChange={(val) => handleChange(val,false,"lunch")}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <div className={styles.header}>
          {!formData.type && 
            <h1>
              Inscription
            </h1>
          }
          {formData.type === 'Magasin' && 
            <h1>
              Inscription en tant que magasin
            </h1>
          }
          {formData.type === 'Fournisseur' && 
            <h1>
              Inscription en tant que fournisseur
            </h1>
          }
          <button
            disabled={!canSubmit}
            className={!canSubmit ? styles.disabled : ''}
            onClick={() => handleSubmit()}
          >
            Valider mon inscription
          </button>
        </div>
        {errorMessage && (
          <p className={styles.error}>
            {errorMessage}
          </p>
        )}
        <div className={styles.contentForm}>
          <h2>Société</h2>
          <div className={styles.form}>
            <div className={styles.row}>
              <div className={styles.containerField}>
                <label>Je m'inscris au salon *</label>
                <Select
                  onChange={(val) => handleChange(val, null, "exhibition")}
                  options={exhibitions}
                  value={formData?.exhibition || null}
                  isSearchable
                  placeholder='Salon...'
                  styles={selectStyle}
                />
              </div>
              <div className={styles.containerField}>
                <label>Je suis *</label>
                <SelectItems
                  items={['Fournisseur', 'Magasin']}
                  selectedItems={formData?.type ? formData?.type : null}
                  uniq
                  required
                  handleChange={(val) => handleChange(val, false, "type")}
                />
              </div>
            </div>
            {(formData.exhibition && formData.type) && (
              <div className={styles.row}>
                <div className={styles.containerField}>
                  <label>Société *</label>
                  <Select
                    onChange={(val) => handleChange(val, null, "company")}
                    options={companies}
                    value={formData?.company || null}
                    isSearchable
                    placeholder='Société...'
                    styles={selectStyle}
                  />
                </div>
              </div>
            )}
            {formData?.type === 'Fournisseur' && formData?.company && (
              <div className={styles.row} style={{marginTop:10}}>
                <div className={styles.containerField}>
                  <InputField
                    id="siret"
                    value={formData.siret || ""}
                    title="Numéro de Siret"
                    placeholder=""
                    type="string"
                    required={true}
                    handleChange={(val, error) => handleChange(val, error, "siret")}
                  />
                </div>
                <div className={styles.containerField}>
                  <InputField
                    id="vatNumber"
                    value={formData.vatNumber || ""}
                    title="Numéro de TVA"
                    placeholder=""
                    type="string"
                    required={true}
                    handleChange={(val, error) => handleChange(val, error, "vatNumber")}
                  />
                </div>
              </div>
            )}
          </div>
          <h2>Mon compte</h2>
          <div className={(!formData.type || !formData.company) 
              ? `${styles.form} ${styles.disabled}` 
              : styles.form
          }>
            <div className={styles.row}>
              <div className={styles.containerField}>
                <InputField
                  id="lastName"
                  value={formData?.profile?.lastName || ""}
                  title="Nom"
                  placeholder=""
                  type="string"
                  textTransform="uppercase"
                  required={true}
                  handleChange={(val, error) => handleChange(val, error, "profile.lastName")}
                />
              </div>
              <div className={styles.containerField}>
                <InputField
                  id="firstName"
                  value={formData?.profile?.firstName || ""}
                  title="Prénom"
                  placeholder=""
                  type="string"
                  textTransform="capitalized"
                  required={true}
                  handleChange={(val, error) => handleChange(val, error, "profile.firstName")}
                />
              </div>
              <div className={styles.containerField}>
                <InputField
                  id="email"
                  value={formData.email || ""}
                  title="Email"
                  placeholder=""
                  type="string"
                  format="email"
                  required={true}
                  disabled={false}
                  handleChange={(val, error) => handleChange(val, error, "email")}
                />
              </div>
            </div>
            <div className={styles.row}>
              <div className={styles.containerField}>
                <InputField
                  value={formData?.profile?.phone || ""}
                  title="Téléphone"
                  placeholder=""
                  type=""
                  maskNumber="## ## ## ## ##"
                  format="phone"
                  handleChange={(val, error) => handleChange(val, error, "profile.phone")}
                />
              </div>
              <div className={styles.containerField}>
                <InputField
                  id="function"
                  value={formData?.profile?.function || ""}
                  title="Fonction"
                  placeholder=""
                  type="string"
                  textTransform="uppercase"
                  required
                  maxLength="30"
                  handleChange={(val, error) => handleChange(val, error, "profile.function")}
                />
              </div>
            </div>
            <div className={styles.row}>
              {formData?.type === 'Magasin' && (
                <div className={styles.containerField}>
                  <label>Lieu de vente<span className="required">*</span></label>
                  <Select
                    onChange={(val) => handleChange(val, null, "profile.activity")}
                    options={activities.map((d) => ({ label: d, value: d }))}
                    value={formData?.profile?.activity || []}
                    isMulti
                    isSearchable
                    styles={selectStyle}
                  />
                </div>
              )}
              {renderPresenceAndLunch()}
            </div>
            {needsPayment &&
              <div className={styles.paymentRow}>
                <button
                  type="button"
                  className={!isValid || isPaymentLoading ? styles.disabled : ''}
                  disabled={!isValid || isPaymentLoading}
                  onClick={handleLunchPayment}
                >
                  {isMultiDaySalon
                    ? `Payer ${unpaidLunchDays.length} déjeuner${unpaidLunchDays.length > 1 ? 's' : ''} (${paymentTotal} €)`
                    : `Payer le déjeuner (${lunchPrice} €)`
                  }
                </button>
              </div>
            }
            {!isMultiDaySalon && lunchPaid && formData?.lunch === 'Oui' && formData?.presence === 'Oui' &&
              <p className={styles.lunchPaid}>Déjeuner payé</p>
            }
            {isMultiDaySalon && lunchDays.length > 0 && unpaidLunchDays.length === 0 &&
              <p className={styles.lunchPaid}>Déjeuners payés</p>
            }
          </div>
        </div>
        {errorMessage && 
          <p className={styles.error}>
            {errorMessage}
          </p>
        }
        <button
          disabled={!canSubmit}
          className={!canSubmit ? styles.disabled : ''}
          onClick={() => handleSubmit()}
        >
          Valider mon inscription
        </button>
      </div>
    </div>
  )
};

export default Register;
